import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ProfileHeader } from "@/components/ProfileHeader";
import { PostCard } from "@/components/PostCard";
import { ReelThumb } from "@/components/ReelThumb";
import { Spinner, EmptyState } from "@/components/Loader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function About({ u }) {
  const tags = [...(u.specializations || []), ...(u.services || []).map((s) => (typeof s === "string" ? s : s?.title)), ...(u.categories || [])].filter(Boolean);
  return (
    <div className="tm-card space-y-6 p-6 sm:p-8">
      <div><h3 className="font-display font-bold text-tm-green">About</h3><p className="mt-2 whitespace-pre-line text-slate-700">{u.bio || "No bio added yet."}</p></div>
      {tags.length > 0 && <div><h3 className="font-display font-bold text-tm-green">Specializations & services</h3>
        <div className="mt-3 flex flex-wrap gap-2">{[...new Set(tags)].map((s) => <span key={s} className="rounded-full bg-tm-muted px-3 py-1.5 text-sm text-tm-green">{s}</span>)}</div></div>}
      {(u.qualification || u.membership_number || u.year_of_qualification) && (
        <div className="grid gap-3 text-sm sm:grid-cols-3">
          {[["Qualification", u.qualification], ["Membership no.", u.membership_number], ["Qualified in", u.year_of_qualification]].filter(([, v]) => v).map(([l, v]) => (
            <div key={l} className="rounded-xl bg-tm-muted p-3"><p className="text-xs text-slate-500">{l}</p><p className="font-semibold text-tm-green">{v}</p></div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Profile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [u, setU] = useState(null);
  const [posts, setPosts] = useState([]);
  const [reels, setReels] = useState([]);

  useEffect(() => {
    setU(null);
    api.get(`/users/${id}`).then(({ data }) => setU(data)).catch(() => setU(false));
    api.get(`/posts/user/${id}`).then(({ data }) => setPosts(data)).catch(() => setPosts([]));
    api.get(`/reels/user/${id}`).then(({ data }) => setReels(data)).catch(() => setReels([]));
  }, [id]);

  if (u === null) return <Spinner />;
  if (u === false) return <EmptyState title="Profile not found" testid="profile-not-found" />;
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <ProfileHeader u={u} setU={setU} isMe={user && user.id === u.id} />
      <Tabs defaultValue="about">
        <TabsList className="border bg-white" data-testid="profile-tabs">
          <TabsTrigger value="about" data-testid="profile-tab-about">About</TabsTrigger>
          <TabsTrigger value="posts" data-testid="profile-tab-posts">Posts ({posts.length})</TabsTrigger>
          <TabsTrigger value="reels" data-testid="profile-tab-reels">Reels ({reels.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="about" className="mt-4"><About u={u} /></TabsContent>
        <TabsContent value="posts" className="mt-4 space-y-4">
          {posts.length ? posts.map((p) => <PostCard key={p.id} post={p} onDelete={(pid) => setPosts((ps) => ps.filter((x) => x.id !== pid))} />) : <EmptyState title="No posts yet" />}
        </TabsContent>
        <TabsContent value="reels" className="mt-4">
          {reels.length ? <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{reels.map((r) => <ReelThumb key={r.id} reel={r} />)}</div> : <EmptyState title="No reels yet" />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
